import { useEffect, useRef, useState } from 'react';
import { isOn, msToNextRotation, nextChange, rotationItem } from '../lib/schedule';
import type { ScreenRotation } from '../services/api';
import { useScreen } from '../store/screen';
import { useSession } from '../store/session';

/** How long a scheduled-off screen stays on after someone touches it or presses a key. */
export const WAKE_MS = 15 * 60_000;

const usePlan = () => {
  const userId = useSession((s) => s.user?.id ?? null);
  const plan = useScreen((s) => (userId ? s.plans[userId] : undefined));
  return { userId, plan };
};

/**
 * Puts on screen what the account chose for it: one layout, or the current item of its
 * rotation (moving on at each interval, by the clock). Paused while settings or the layout
 * editor are open, so a switch never lands in the middle of someone's changes.
 */
export function useRotation(paused: boolean): void {
  const { userId, plan } = usePlan();
  const rotationKey = JSON.stringify(plan?.rotation ?? null);
  const presetId = plan?.presetId ?? null;
  const known = plan !== undefined;

  useEffect(() => {
    // Not checked in since this was added: keep whatever it showed before.
    if (!userId || !known || paused) return;
    const setAssignment = useScreen.getState().setAssignment;
    const rotation = JSON.parse(rotationKey) as ScreenRotation | null;
    if (!rotation || rotation.items.length < 2) {
      setAssignment(userId, presetId);
      return;
    }
    let timer: number | undefined;
    const tick = () => {
      const item = rotationItem(rotation, Date.now());
      setAssignment(userId, item === 'main' ? null : item);
      timer = window.setTimeout(tick, msToNextRotation(rotation, Date.now()) + 50);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [userId, known, paused, rotationKey, presetId]);
}

/**
 * Whether this screen's schedule has it switched off right now. Off, the display goes black
 * and stops (useWakeLock lets the device sleep it). A touch or key press wakes it for
 * WAKE_MS – that first press only wakes it – and each later one extends that. Never off
 * while `inUse` (settings or the layout editor open).
 */
export function useScheduledOff(inUse = false): { off: boolean; awakeUntil: number | null } {
  const { plan } = usePlan();
  const schedule = plan?.schedule ?? null;
  const scheduleKey = JSON.stringify(schedule);
  const [now, setNow] = useState(() => Date.now());
  const [awakeUntil, setAwakeUntil] = useState<number | null>(null);
  const scheduledOff = !isOn(schedule, new Date(now));
  const awake = scheduledOff && awakeUntil !== null && awakeUntil > now;
  const off = scheduledOff && !awake && !inUse;

  // Look again every 30 s and exactly at the next switch-over or wake expiry.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => {
    const next = nextChange(schedule, new Date(now))?.getTime();
    const due = [next, awake ? awakeUntil! : undefined].filter((x): x is number => x !== undefined);
    if (!due.length) return;
    const t = window.setTimeout(() => setNow(Date.now()), Math.max(0, Math.min(...due) - Date.now()) + 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleKey, now, awake, awakeUntil]);

  // Wake on use while scheduled off. Capture phase: the press that wakes the screen doesn't
  // also reach the slideshow's shortcuts (OK would otherwise open settings).
  const offRef = useRef(off);
  offRef.current = off;
  useEffect(() => {
    if (!scheduledOff) {
      setAwakeUntil(null);
      return;
    }
    const wake = (e: Event) => {
      if (offRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
      setAwakeUntil(Date.now() + WAKE_MS);
      setNow(Date.now());
    };
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    events.forEach((ev) => window.addEventListener(ev, wake, { capture: true }));
    return () => events.forEach((ev) => window.removeEventListener(ev, wake, { capture: true }));
  }, [scheduledOff]);

  useEffect(() => useScreen.getState().setOff(off), [off]);
  return { off, awakeUntil: awake ? awakeUntil : null };
}
